/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import Highcharts from 'highcharts-pack/highcharts/highcharts';
import fontUtils from 'js-sdk/src/common/util/fontUtils';
// @ts-ignore
import chartLayouts from '../../../../api/chart/adhocToHighchartsAdapter/gaugeCharts/supportedLayoutsEnum';

const getChartAreaSize = (options :any) => {

    const {
        amountOfCharts,
        chartUserSettings
    } = options;

    let {
        aspectRation,
        containerWidth,
        containerHeight,
    } = options;

    containerWidth -= (chartUserSettings.plotOffsets.left + chartUserSettings.plotOffsets.right);
    containerHeight -= (chartUserSettings.plotOffsets.top + chartUserSettings.plotOffsets.bottom);

    // The 'aspectRation' is the requested aspect ratio for the area we are trying to find out.
    // Introducing 'aspectRation' allows us to find area not only for square areas but also for rectangular.
    // We threat 'aspectRation' as "horizontal side / vertical side",
    // so the 'vertical' = 'horizontal' / aspectRation
    // and 'horizontal' = 'vertical' * aspectRation
    if (typeof aspectRation === 'undefined') {
        aspectRation = 1;
    }

    // Test for invalid input: check what amount of pixels in 'width * height' area is less than
    // the squares we need to place on this area
    if (containerWidth * containerHeight < amountOfCharts) {
        return 0;
    }

    let layout = chartLayouts.BEST_FIT;
    const supportedLayouts = Object.values(chartLayouts);
    if (chartUserSettings.layout && supportedLayouts.indexOf(chartUserSettings.layout) !== -1) {
        // eslint-disable-next-line prefer-destructuring
        layout = chartUserSettings.layout;
    }

    let byVertical = 0;
    let byHorizontal = 0;
    let chartAreaSize = 0;

    if (layout === chartLayouts.BEST_FIT) {

        containerHeight *= aspectRation;

        // Initial guess.
        const containerAspect = containerHeight / containerWidth;
        const xf = Math.sqrt(amountOfCharts / containerAspect);
        const yf = xf * containerAspect;
        let x = Math.max(1.0, Math.floor(xf));
        let y = Math.max(1.0, Math.floor(yf));
        let xSize = Math.floor(containerWidth / x);
        let ySize = Math.floor(containerHeight / y);
        chartAreaSize = Math.min(xSize, ySize);

        // Test our guess:
        x = Math.floor(containerWidth / chartAreaSize);
        y = Math.floor(containerHeight / chartAreaSize);
        // We guessed too high.
        if (x * y < amountOfCharts) {
            if (((x + 1) * y < amountOfCharts) && (x * (y + 1) < amountOfCharts)) {
                // Case 2: the upper bound is correct compute the chartAreaSize that will result in (x+1)*(y+1) tiles.
                xSize = Math.floor(containerWidth / (x + 1));
                ySize = Math.floor(containerHeight / (y + 1));

                chartAreaSize = Math.min(xSize, ySize);
            } else {
                // Case 3: solve an equation to determine the final x and y dimensions and then compute
                // the chartAreaSize that results in those dimensions.
                const testX = Math.ceil(amountOfCharts / y);
                const testY = Math.ceil(amountOfCharts / x);
                xSize = Math.min(Math.floor(containerWidth / testX), Math.floor(containerHeight / y));
                ySize = Math.min(Math.floor(containerWidth / x), Math.floor(containerHeight / testY));

                chartAreaSize = Math.max(xSize, ySize);
            }
        }

        byHorizontal = chartAreaSize;
        byVertical = Math.floor(chartAreaSize / aspectRation);
    }

    if (layout === chartLayouts.HORIZONTAL) {
        byHorizontal = Math.floor(containerWidth / amountOfCharts);
        if (containerHeight * aspectRation < byHorizontal) {
            byHorizontal = Math.floor(containerHeight * aspectRation);
        }
        byVertical = Math.floor(byHorizontal / aspectRation);
    }

    if (layout === chartLayouts.VERTICAL) {
        byVertical = Math.floor(containerHeight / amountOfCharts);
        if (containerWidth / aspectRation < byVertical) {
            byVertical = Math.floor(containerWidth / aspectRation);
        }
        byHorizontal = Math.floor(byVertical * aspectRation);
    }

    return {
        byVertical,
        byHorizontal
    };
};

const getGaugeChartPositions = (options :any) => {

    const {
        amountOfCharts,
        chartAreaSize,
        containerWidth,
        containerHeight,
        sizes,
        chartUserSettings
    } = options;

    const availableWidth = containerWidth - (chartUserSettings.plotOffsets.left + chartUserSettings.plotOffsets.right);
    const availableHeight = containerHeight - (chartUserSettings.plotOffsets.top + chartUserSettings.plotOffsets.bottom);

    let layout = chartLayouts.BEST_FIT;
    const supportedLayouts = Object.values(chartLayouts);
    if (chartUserSettings.layout && supportedLayouts.indexOf(chartUserSettings.layout) !== -1) {
        // eslint-disable-next-line prefer-destructuring
        layout = chartUserSettings.layout;
    }

    // calculate how many charts we can place in the container
    let chartsByHorizontal;
    let chartsByVertical;

    if (layout === chartLayouts.HORIZONTAL) {
        chartsByHorizontal = Math.floor(availableWidth / chartAreaSize.byHorizontal);
        chartsByVertical = 1;
    } else if (layout === chartLayouts.VERTICAL) {
        chartsByHorizontal = 1;
        chartsByVertical = Math.floor(availableHeight / chartAreaSize.byVertical);
    } else {
        chartsByHorizontal = Math.floor(availableWidth / chartAreaSize.byHorizontal);
        chartsByVertical = Math.ceil(amountOfCharts / chartsByHorizontal);
    }

    // if the container is too big and we are rendering just one chart then we need to correct values which we got:
    chartsByHorizontal = Math.min(chartsByHorizontal, amountOfCharts);
    chartsByVertical = Math.min(chartsByVertical, amountOfCharts);

    const horizontalGap = (availableWidth - chartAreaSize.byHorizontal * chartsByHorizontal) / (chartsByHorizontal + 1);
    const verticalGap = (availableHeight - chartAreaSize.byVertical * chartsByVertical) / (chartsByVertical + 1);

    const positions = [];

    for (let i = 0; i < amountOfCharts; i += 1) {

        // we are dealing with multiplication and the math requires us to
        // start counting from 1 (not from 0, as the binary system does)
        const chartNumber = i + 1;

        const xPositionNumber = chartNumber % chartsByHorizontal === 0 ? chartsByHorizontal : chartNumber % chartsByHorizontal;
        const yPositionNumber = Math.floor(chartNumber / chartsByHorizontal) + (chartNumber % chartsByHorizontal === 0 ? 0 : 1);

        // we use 'chartAreaSize. ...' divided by 2 because we want to take the center
        let xCenterPosition = chartAreaSize.byHorizontal * (xPositionNumber - 1) + chartAreaSize.byHorizontal / 2 + horizontalGap * xPositionNumber;
        let yCenterPosition = chartAreaSize.byVertical * (yPositionNumber - 1) + chartAreaSize.byVertical / 2 + verticalGap * yPositionNumber;

        xCenterPosition += chartUserSettings.plotOffsets.left;
        yCenterPosition += chartUserSettings.plotOffsets.top + sizes.verticalCenterOffset;

        positions.push({
            // added for debugging purpose
            xCenter: Math.round(xCenterPosition),
            yCenter: Math.round(yCenterPosition),

            left: Math.round(xCenterPosition - chartAreaSize.byHorizontal / 2),
            top: Math.round(yCenterPosition - (chartAreaSize.byVertical / 2 + sizes.verticalCenterOffset)),

            // don't round positions in percents because this will lead to miss-positioning
            xCenterInPercent: (xCenterPosition / containerWidth) * 100,
            yCenterInPercent: (yCenterPosition / containerHeight) * 100
        })
    }

    return positions;
};

const calculateFontData = (options :any) => {

    const {
        texts,
        formattingOptions,
        autoScaleStrategy,
        fonts,
        constrains
    } = options;

    let {
        fieldFontSize,
        valueFontSize,
        measureFontSize
    } = fonts;
    const { minimalFontSize } = fonts;

    const fieldColor = (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black';
    const valueColor = (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black';
    const measureColor = (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black';

    const fontData :any = [];

    if (!formattingOptions.autoScaleFonts) {
        texts.forEach(() => {
            fontData.push({
                field: {
                    fontSize: fieldFontSize,
                    color: fieldColor
                },
                value: {
                    fontSize: valueFontSize,
                    color: valueColor
                },
                measure: {
                    fontSize: measureFontSize,
                    color: measureColor
                }
            });
        });

        return fontData;
    }

    // form structures to collect min/max font
    const fieldFonts = [];
    const valueFonts = [];
    const measureFonts = [];

    texts.forEach((text :any) => {

        if (text.field) {
            const fontForField = fontUtils.findFontSize({
                text: text.field,
                sizeUnits: 'px',
                fontWeight: 'normal',
                lineHeight: 'normal',
                minimalFontSize,
                fontCheckingStrategy: 'basedOnFontHeight',
                widthAvailable: constrains.field.width,
                heightAvailable: constrains.field.height
            });
            fieldFonts.push(fontForField);
        }

        if (text.value) {
            const fontForValue = fontUtils.findFontSize({
                text: text.value,
                sizeUnits: 'px',
                fontWeight: 'bold',
                lineHeight: 'normal',
                minimalFontSize,
                fontCheckingStrategy: 'basedOnFontHeight',
                widthAvailable: constrains.value.width,
                heightAvailable: constrains.value.height
            });
            valueFonts.push(fontForValue);
        }

        if (text.measure) {
            const fontForMeasure = fontUtils.findFontSize({
                text: text.measure,
                sizeUnits: 'px',
                fontWeight: 'normal',
                lineHeight: 'normal',
                minimalFontSize,
                fontCheckingStrategy: 'basedOnFontHeight',
                widthAvailable: constrains.measure.width,
                heightAvailable: constrains.measure.height
            });
            measureFonts.push(fontForMeasure);
        }
    });

    if (fieldFonts.length === 0) {
        fieldFonts.push({
            fontSize: fieldFontSize
        });
    }
    if (valueFonts.length === 0) {
        valueFonts.push({
            fontSize: valueFontSize
        });
    }
    if (measureFonts.length === 0) {
        measureFonts.push({
            fontSize: measureFontSize
        });
    }

    if (autoScaleStrategy === 'minimalFontForAll') {
        fieldFontSize = Math.min(...fieldFonts.map((font) => font.fontSize));
        valueFontSize = Math.min(...valueFonts.map((font) => font.fontSize));
        measureFontSize = Math.min(...measureFonts.map((font) => font.fontSize));
    } else if (autoScaleStrategy === 'maximalFontForAll') {
        fieldFontSize = Math.max(...fieldFonts.map((font) => font.fontSize));
        valueFontSize = Math.max(...valueFonts.map((font) => font.fontSize));
        measureFontSize = Math.max(...measureFonts.map((font) => font.fontSize));
    }
    // in other case (else) the font size will get initial value as indication what
    // user has specified wrong parameters

    texts.forEach(() => {
        fontData.push({
            field: {
                fontSize: fieldFontSize,
                color: fieldColor
            },
            value: {
                fontSize: valueFontSize,
                color: valueColor
            },
            measure: {
                fontSize: measureFontSize,
                color: measureColor
            }
        });
    });

    return fontData;
};

export default {
    getChartAreaSize,
    getGaugeChartPositions,
    calculateFontData
};
